import React, { useEffect } from "react";
import {
  NotificationContent,
  NotificationHeader,
  ToastWrapper,
  StatusText,
  ToastIcon,
} from "./ErrorToastStyled";
import { X, CheckCircle, XCircle, Copy } from "@phosphor-icons/react";
import { BackButton } from "../../pages/MatterDetails/MatterDetailsStyled";
import PropTypes from "prop-types";
import styled from "styled-components";
import { formatMessage } from "../../utils/formatSnackbarMsg";

// Container for all stacked toasts
const ToastStack = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  display: flex;
  flex-direction: column-reverse;
  gap: 10px;
  z-index: 2500;
`;

const CopyErrorButton = styled.button`
  align-items: center;
  background: #f3f6fb;
  border: 1px solid #d7e0ee;
  border-radius: 6px;
  color: #0c53bb;
  cursor: pointer;
  display: inline-flex;
  font-family: Manrope, sans-serif;
  font-size: 12px;
  font-weight: 600;
  gap: 4px;
  margin-left: 12px;
  padding: 6px 8px;
  white-space: nowrap;
`;

const ErrorToast = ({ message, onClose }) => {
  useEffect(() => {
    if (message.length === 0) return;

    const timers = message.map((msg) =>
      setTimeout(() => {
        onClose(msg.id);
      }, msg.copyText ? 8000 : 3000)
    );

    return () => {
      for (const timer of timers) {
        clearTimeout(timer); // clean up all timers
      }
    };
  }, [message, onClose]);

  return (
    <ToastStack data-testid="message-toast">
      {message?.map((msg) => (
        <ToastWrapper key={msg.id}>
          <NotificationHeader>
            <ToastIcon>
              {msg?.success ? (
                <CheckCircle size={24} weight="fill" color="#10B981" data-testid="toast-success-icon" />
              ) : (
                <XCircle size={24} weight="fill" color="#EF4444" data-testid="toast-error-icon" />
              )}
            </ToastIcon>
            <NotificationContent>
              <StatusText  style={{ maxWidth: "600px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {formatMessage(msg?.newMsg)}</StatusText>
            </NotificationContent>
            {msg?.copyText && (
              <CopyErrorButton
                type="button"
                onClick={() => navigator.clipboard?.writeText?.(msg.copyText)}
                aria-label={msg.copyLabel || "Copy error"}
              >
                <Copy size={14} weight="bold" />
                {msg.copyLabel || "Copy error"}
              </CopyErrorButton>
            )}
          </NotificationHeader>
          <BackButton>
            <X size={20} onClick={() => onClose(msg.id)} data-testid="message-toast-close"/>
          </BackButton>
        </ToastWrapper>
      ))}
    </ToastStack>
  );
};

ErrorToast.propTypes = {
  message: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      newMsg: PropTypes.string.isRequired, // updated to match your usage
      success: PropTypes.bool, // optional if some messages are success
      copyText: PropTypes.string,
      copyLabel: PropTypes.string,
    })
  ).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ErrorToast;
